
  import { defineComponent, ref, watch, watchEffect, computed, onUnmounted, onMounted } from "vue";
  import { useStore } from "vuex";
  import {ElIcon, ElInput} from "element-plus";
  import { Check, WarnTriangleFilled } from '@element-plus/icons-vue';
  import { FormActions, FormGetters } from './../../Store/index';
  import ModalComponent from "./ModalComponent.vue";
  import {
    addNewValueToFunnelStep,
    updateStoreValueAndFilter,
    useShowElementByPredefinedManufacturer,
    callPaq,
    validation, resetInputs, getInputsAboveOrder, setResettingFormToFalseWhenNoRequests
  } from './../../Composables/form';
  import { isEmpty, stringToBoolean } from './../../Composables/functions';

  export default defineComponent({
    name: "RadioComponent",
    components: {
      ElInput,
      ModalComponent,
      ElIcon,
      Check,
      WarnTriangleFilled
    },

    props: {
      options: {
        type: Object,
        required: true,
      },
      value: String,
      name: {
        type: String,
        default: ''
      },
      title: String,
      errorMessage: String,
      divClass: String,
      id: String,
      gap: String,
      labelClass: String,
      modalBtnTitle: String,
      modalClass: String,
      modalTitle: String,
      modalText: String,
      modalConfirmButton: String,
      modalConfirmButtonClass: String,
      modalCancelButton: String,
      modalCancelButtonClass: String,
      modalIconBefore: String,
      showConfirmButton: Boolean,
      showCancelButton: Boolean,
      filter: Boolean,
      disabled: Boolean,
      stateAutoSelected: Boolean,
      isComponentInComponent: {
        type: Boolean,
        required: false,
        default: false
      },
      required: {
        type: Boolean,
        required: false,
        default: false
      },
      filterProperty: {
        type: Array,
        required: false,
        default: () => ['', '']
      },
      defaultValue: {
        type: String,
        default: "",
      },
    },

    setup(props) {
      // Access the Vuex store
      const store = useStore();

      // Getters from the store
      const labels = store.getters[`form/${FormGetters.GET_LABELS}`],
          icons = store.getters[`form/${FormGetters.GET_ICONS}`],
          filterName = (props.filterProperty[1] as string).toString();

      // Set modalText and modalTitle from props or to empty strings if not provided
      const modalText = props.modalText ?? "";
      const modalTitle = props.modalTitle ?? "";

      // Function to check if options contain values
      const hasEmptyOptions = (options: any) => {
        return (
            (options instanceof Map && (!options.size || options.size === 0)) && Object.keys(options).length === 0 ||
            (!(options instanceof Map) && Object.keys(options).length === 0)
        );
      };

      // Check if the component should be clearable
      const handleClearable = () => {
        clearable.value = !!selectedOption.value && !stateAutoSelected.value && isClickedByPerson.value;
      };

      const clear = async () => {
        await store.dispatch(`form/${FormActions.SET_IS_RESSETING_FORM}`, true);

        // Reset values
        resetValues();

        let value = stringToBoolean(selectedOption.value);

        if (props.isComponentInComponent) {
          value = 'resetValue';
        }

        // Update store value and filter
        await updateStoreValueAndFilter(store, props, value, isValid.value, false, true);

        await validation(store);

        // Reset Inputs
        resetInputs(store, getInputsAboveOrder(store, props.name));

        await setResettingFormToFalseWhenNoRequests(store);

        // function whereit focus on first input in parent element
        focusFirstInput();
      };

      const parentRef = ref(null);

      const focusFirstInput = () => {
        if (parentRef.value) {
          const firstInput = (parentRef.value as HTMLElement).querySelector('input');
          if (firstInput) {
            firstInput.focus();
          }
        }
      };

      // Function to reset values
      const resetValues = () => {
        isValid.value = false;
        selectedOption.value = '';
        isTouched.value = false;
        stateAutoSelected.value = false;

        const filter = store.getters[`form/${FormGetters.GET_DATA}`].filter.value;

        if ((isEmpty(filter) || isEmpty(filter[filterName]))) {
          store.dispatch(`form/${FormActions.DELETE_DATA}`, {field: 'filter', key: 'value', value: filterName});
        }
      };

      const resetIsClickedByPerson = () => {
        isClickedByPerson.value = false;
      };

      const validateRadio = () => {
        if (props.required) {
          isValid.value = selectedOption.value !== undefined && selectedOption.value.trim() !== "";
        } else {
          // If not required, consider it always valid
          isValid.value = true;
        }
      };

      // Use onMounted to add event listener when component is mounted
      onMounted(async () => {
        // Default value init
        if (props.defaultValue) {
          await updateValue(props.defaultValue, false);
        }

        showElement.value = useShowElementByPredefinedManufacturer(store, props.name).value;

        // _paq
        if (filterName && selectedOption.value) {
          callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter',filterName);
        }
      });

      // Initialize variables with refs
      let isValid = ref(false),
          options = ref({}),
          isTouched = ref(false),
          stateAutoSelected = ref(false),
          selectedOption = ref<string>(''),
          showElement = ref(true),
          showError = ref(false),
          wasClickedOnShowResultPage = ref(store.getters[`form/${FormGetters.GET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`]),
          clearable = ref(true),
          isClickedByPerson = ref(false);

      const showErrorMessage = () => {
        showError.value = (isTouched && !isValid) || (wasClickedOnShowResultPage.value && !isValid.value);
      };

      // Function to handle the update of the selected value
      const updateValue = async (eventValue: any, fetchNewData: boolean = true) => {
        await store.dispatch(`form/${FormActions.SET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`, false);

        let selectedValue = '';
        let isClickedByPersonTmp = false;

        if (eventValue instanceof Event && 'target' in eventValue && eventValue.target instanceof HTMLInputElement) {
          selectedValue = (eventValue.target as HTMLInputElement).value;
          isClickedByPersonTmp = true;
        } else if (eventValue) {
          // Convert eventValue to a string
          selectedValue = eventValue.toString();
        }

        // Extract all values from objects within the array
        const allValues = Object.values(options.value).map(obj => (obj as { value: string }).value);

        // Check if the selected value is the same as the current value
        const valueWasChanged = stringToBoolean(selectedValue) !== stringToBoolean(selectedOption.value);

        //if klicked on selected isCar or useHsnTsn do nothing because it cant be unchecked
        if(eventValue && eventValue.target && (eventValue.target.getAttribute('name') == 'isCar' || eventValue.target.getAttribute('name') == 'useHsnTsn') && !valueWasChanged) {
            return;
        }


        // Check if the selected value is the same as the current value
        if (
            (!valueWasChanged && isClickedByPerson.value) ||
            (!props.isComponentInComponent && !allValues.includes(stringToBoolean(selectedValue))) ||
            !selectedValue
        ) {
          // If yes, reset radio button values
          await clear();
        } else {
          resetIsClickedByPerson();

          // If no, update the selected option value
          selectedOption.value = selectedValue;

          // Update the validity based on whether the value is empty or not
          validateRadio();

          // Mark the field as touched
          isTouched.value = !isEmpty(selectedOption.value);
        }

        if (!props.isComponentInComponent && !isEmpty(selectedOption.value)) {
          // Asynchronously update the store value and apply filters
          await updateStoreValueAndFilter(store, props, stringToBoolean(selectedOption.value), isValid.value, false, true, !!props.defaultValue, fetchNewData);

          // _paq
          if (filterName && selectedOption.value && !store.getters[`form/${FormGetters.GET_IS_RESETTING_FORM}`]) {
            callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', filterName);
          }

          addNewValueToFunnelStep(store, filterName, selectedOption.value);

          await validation(store);
        }

        isClickedByPerson.value = isClickedByPersonTmp;
        stateAutoSelected.value = !isClickedByPersonTmp;

        handleClearable();
        showErrorMessage();
      }

      // Watch for changes in the value
      watch(() => store.getters[`form/${FormGetters.GET_INPUTS}`][props.name].value, (newValue, oldValue) => {
        if (newValue === 'resetValue') {
          resetValues();
          updateValue(newValue);
        }
      });

      watch(() => props.value, async (newValue) => {
        if (newValue !== selectedOption.value) {
          await updateValue(newValue);
        }
      });

      watch(() => store.getters[`form/${FormGetters.GET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`], async (newValue, oldValue) => {
        wasClickedOnShowResultPage.value = newValue;
        showErrorMessage();
      });

      // Watch for changes in props.options
      watchEffect(async () => {
        let tmpOptions = {};

        if (props.filter) {
          if (Object.keys(props.options).length !== 0) {
            resetValues();
            props.options.forEach((item) => {
              if (props.filterProperty && item.key === props.filterProperty[1]) {
                if (item.proposals) {
                  tmpOptions = item.proposals.reduce((obj, item, index) => {
                    obj[index] = {
                      label: item.v,
                      value: item.k
                    };
                    return obj;
                  }, {});
                }
                return;
              }
            });
          }
        } else {
          tmpOptions = props.options;
        }

        if (Object.entries(tmpOptions).length === 1) {
          if (
              ((stateAutoSelected.value === false && isEmpty(selectedOption.value)) || stateAutoSelected.value)
              && selectedOption.value !== tmpOptions[Object.keys(tmpOptions)[0]]['value']
          ) {
            // Only one option is available, so select it
            selectedOption.value = tmpOptions[Object.keys(tmpOptions)[0]]['value'];
            isValid.value = true;
            isTouched.value = true;
            stateAutoSelected.value = !isClickedByPerson.value;

            if (!props.isComponentInComponent && !isEmpty(selectedOption.value)) {
              await updateStoreValueAndFilter(store, props, selectedOption.value, isValid.value, stateAutoSelected.value, false);
              addNewValueToFunnelStep(store, filterName, selectedOption.value);
            }
          }
        } else if (hasEmptyOptions(tmpOptions)) {
          // No options available, reset values
          resetValues();
        }

        options.value = tmpOptions;
        handleClearable();
      });

      // Convert options to an array of key-value pairs
      const optionsArray = computed(() => {
        if (options.value instanceof Map) {
          return Array.from(options.value.values());
        } else if (typeof options.value === 'object') {
          return Object.values(options.value);
        }
        return [];
      });

      return {
        icons,
        labels,
        isValid,
        isTouched,
        modalText,
        modalTitle,
        selectedOption,
        optionsArray,
        showElement,
        clearable,
        showError,
        hasEmptyOptions,
        clear,
        updateValue,
        parentRef
      }
    }
  })
